// Thin fetch wrapper for /api/* calls. Sprint 11b: supports both same-origin
// (single-server) and cross-origin (multi-server federation via CF Pages).
// The baseUrl is resolved from the active server in useServersStore; when
// empty, paths are relative (same-origin).

import type { Agent, ApiError, EffortLevel, Message, PermissionMode, WorkdirIndexResponse } from '../types';
import { getActiveBaseUrl } from '../stores/servers.store';

export class ApiHttpError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function parseError(res: Response): Promise<ApiHttpError> {
  let code = 'HTTP_ERROR';
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as Partial<ApiError>;
    if (body && typeof body === 'object' && body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
    }
  } catch {
    // not JSON, keep defaults
  }
  return new ApiHttpError(res.status, code, message);
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  signal?: AbortSignal;
  /** Override the base URL for this single request. */
  baseUrl?: string;
  /** Cabeceras extra (p. ej. Idempotency-Key al contratar). No pueden pisar las de seguridad. */
  headers?: Record<string, string>;
}

// Sprint 5.5 (H3): the backend rejects state-changing requests that don't
// carry this header. Browsers refuse to attach custom headers on cross-site
// form/img/etc. requests, so it doubles as CSRF protection for the cookie
// session. Sent on EVERY request (GETs included) — cheap and uniform.
const CSRF_HEADER = 'X-Dashboard-Request';

// Prefijo de la API. Vacío en producción (las rutas son /api/...). En un
// despliegue de PRUEBAS se compila con VITE_API_PREFIX=/api-v3 y todas las
// llamadas salen por ahí, donde escucha el servidor de pruebas — así la rama
// nueva se puede revisar en el mismo dominio sin tocar el backend vivo.
const API_PREFIX: string = import.meta.env.VITE_API_PREFIX ?? '';

/** Aplica el prefijo de pruebas a una ruta /api/... (idempotente). */
export function rutaApi(path: string): string {
  if (!API_PREFIX || !path.startsWith('/api')) return path;
  return `${API_PREFIX}${path.slice('/api'.length)}`;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, baseUrl } = opts;
  const base = baseUrl ?? getActiveBaseUrl();
  const headers: Record<string, string> = { ...(opts.headers ?? {}), [CSRF_HEADER]: '1' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };
  if (signal) init.signal = signal;

  const res = await fetch(`${base}${rutaApi(path)}`, init);
  if (!res.ok) throw await parseError(res);

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return undefined as T;
  return (await res.json()) as T;
}

// ---------- Agents (chat-view helpers, S4) ---------------------------------

// Sprint 15 — initial load returns the smart-window messages PLUS the
// `lastUserMessageId` (sticky anchor invariant) and `hasMore` (lets the
// paginated history hook stop at the agent's first message).
// Sprint 15 — composite-cursor pagination. The legacy `before` (timestamp
// only) is still accepted by the backend during the migration window but
// the preferred call uses `(beforeTs, beforeId)` so same-ms ties at the
// page boundary do not produce duplicates.
export interface FetchAgentMessagesResponse {
  messages: Message[];
  hasMore: boolean;
}

export function fetchAgentMessages(
  id: string,
  opts: { beforeTs?: string; beforeId?: string; limit?: number } = {},
): Promise<FetchAgentMessagesResponse> {
  const params = new URLSearchParams();
  if (opts.beforeTs) params.set('beforeTs', opts.beforeTs);
  if (opts.beforeId) params.set('beforeId', opts.beforeId);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return api<FetchAgentMessagesResponse>(
    `/api/agents/${encodeURIComponent(id)}/messages${qs ? `?${qs}` : ''}`,
  );
}

// Sprint 8 — multipart upload for one chip. Uses a raw fetch (not the
// `api()` helper) because we need multipart encoding + an AbortSignal
// hook + same auth/CSRF treatment.
export interface UploadAttachmentResp {
  chipId: string;
  route: 'image' | 'pdf' | 'text-inline' | 'disk';
  attachment: {
    id: string;
    route: 'image' | 'pdf' | 'text-inline' | 'disk';
    name: string;
    displayName: string;
    size: number;
    mimeType: string;
    sha256: string;
    diskPath?: string;
    pageCount?: number;
    textPreview?: string;
  };
}

/** POST de un cuerpo binario (p. ej. una nota de voz) con la misma cabecera CSRF y los mismos errores. */
export async function postBinario<T>(path: string, cuerpo: Blob, contentType: string): Promise<T> {
  const res = await fetch(`${getActiveBaseUrl()}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { [CSRF_HEADER]: '1', 'Content-Type': contentType },
    body: cuerpo,
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

export async function uploadAttachment(opts: {
  agentId: string;
  chipId: string;
  messageId: string;
  file: File;
  signal?: AbortSignal;
}): Promise<UploadAttachmentResp> {
  const base = getActiveBaseUrl();
  const url = `${base}/api/agents/${encodeURIComponent(opts.agentId)}/attachments`;
  const fd = new FormData();
  fd.append('chipId', opts.chipId);
  fd.append('messageId', opts.messageId);
  fd.append('displayName', opts.file.name);
  fd.append('file', opts.file);
  // No Content-Type header — the browser auto-sets the multipart boundary.
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { [CSRF_HEADER]: '1' },
    body: fd,
    signal: opts.signal,
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as UploadAttachmentResp;
}

export function forkAgent(id: string, name?: string): Promise<{ agent: Agent }> {
  return api(`/api/agents/${encodeURIComponent(id)}/fork`, {
    method: 'POST',
    body: name ? { name } : {},
  });
}

export function deleteAgentRequest(id: string): Promise<void> {
  return api(`/api/agents/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Sprint 5.5 (B6): kills the underlying Claude process WITHOUT soft-deleting
// the agent row. Use for the Stop button in ChatHeader so the agent stays
// visible in the sidebar (and forkable) after stopping.
export function stopAgentRequest(id: string): Promise<void> {
  return api(`/api/agents/${encodeURIComponent(id)}/stop`, { method: 'POST' });
}

export function renameAgentRequest(
  id: string,
  name: string,
): Promise<{ agent: Agent }> {
  return api(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { name },
  });
}

// Sprint 20.3 PR-6 (D013-EC-44 / EC-48) — manual failover. Triggered by
// the "Try another runner" button in the runner-offline banner. The
// backend picks a NEW runner via the scheduler (excluding the current
// one), atomically rewrites runner_id + previous_runner_id, persists the
// "🔁 Reasignado…" system message, and respawns with --resume so the
// chat continues seamlessly.
export interface FailoverAgentResponse {
  agent: Agent;
  failover: {
    fromRunnerId: string;
    toRunnerId: string;
    fromHostname: string | null;
    toHostname: string;
    systemMessageId: string;
  };
}

export function failoverAgentRequest(id: string): Promise<FailoverAgentResponse> {
  return api(`/api/agents/${encodeURIComponent(id)}/failover`, { method: 'POST' });
}

// ---------- Sprint 20.3 PR-8 — Admin runners panel ------------------------
//
// Wire-shape contract mirrors `RunnerSnapshot` in
// `backend/src/api/runners.routes.ts`. Re-declared here to keep the
// frontend free of backend imports (per S2 baseline convention).

export type RunnerStatus = 'online' | 'offline' | 'draining' | 'incompatible_version';

export type RunnerUnavailableReason =
  | 'claude_account_blocked'
  | 'disk_full'
  | 'claude_not_authenticated'
  | 'incompatible_version'
  // Sprint runner-resilience-after-missing-credential §2.4 — close the
  // backend↔frontend enum drift introduced by sprint
  // runner-credential-bootstrap. Backend has emitted this value since
  // migration 029, but this enum was never updated; when a runner
  // self-marked `missing_credential` the Settings page crashed with
  // "Cannot read properties of undefined (reading 'split')" because
  // `t(UNAVAILABLE_LABEL_KEY[runner.unavailableReason])` resolved to
  // `t(undefined)` and i18n's resolve() threw.
  | 'missing_credential'
  // Sprint runner-op-token-single-source (defect 3). Backend emits this
  // value (migration 036) when the runner's `op whoami` returns a
  // workspace URL other than the expected `santi-team.1password.com`.
  // Operator action required: rotate /etc/asoasis/op-token to a token
  // from the correct workspace.
  | 'wrong_workspace';

export interface RunnerSnapshot {
  id: string;
  hostname: string;
  status: RunnerStatus;
  protocolVersion: number;
  claudeAccountId: string | null;
  cap5hResidualPct: number | null;
  // Sprint 20.3 PR-10 (D17) — ISO 8601 timestamp at which the runner's
  // 5h rolling cap resets. Surfaced for the centralized usage modal's
  // "Earliest reset" footer. NULL when the runner has never sent a v3
  // heartbeat with the cap reset metadata.
  cap5hResetAt: string | null;
  weeklyHoursSonnet: number | null;
  weeklyHoursOpus: number | null;
  tpmHeadroomPct: number | null;
  activeAgents: number;
  diskPctUsed: number | null;
  drainMode: boolean;
  unavailableReason: RunnerUnavailableReason | null;
  lastSeen: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export function fetchRunners(): Promise<{ runners: RunnerSnapshot[] }> {
  return api('/api/runners');
}

// Sprint 20.8 (D013-EC-76) — wire shape returned by GET /api/runners/usage.
// One row per runner. `usage` carries the real per-bucket Anthropic
// payload from the runner (null on failure). `usageErrorCode` is set
// only when `usage === null`; on success it is omitted entirely.
export type RunnerUsageErrorCode =
  | 'OAUTH_TOKEN_MISSING'
  | 'OAUTH_TOKEN_INVALID'
  | 'RATE_LIMITED'
  | 'UPSTREAM_ERROR'
  | 'TIMEOUT'
  | 'RUNNER_OFFLINE'
  | 'BRIDGE_STOPPED'
  | 'BRIDGE_UNCONFIGURED';

export interface RunnerUsageEntry {
  runnerId: string;
  hostname: string;
  status: RunnerStatus;
  claudeAccountId: string | null;
  drainMode: boolean;
  lastSeen: string | null;
  usage: UsageData | null;
  usageErrorCode?: RunnerUsageErrorCode;
  // 22-10-2026 — panel de cuentas de Claude (backend/src/usage/usage-cache.ts `anotar`).
  cuenta?: CuentaClaude | null;
  leidoEn?: string;
  ultimaLecturaOk?: string | null;
  fallandoDesde?: string | null;
  /** Última lectura buena si tiene menos de 10 min (solo para el panel). */
  usoReciente?: UsageData | null;
}

/** Qué cuenta de Claude tiene el runner (backend/src/runner/health/anthropic-profile.ts). */
export interface CuentaClaude {
  nombre: string | null;
  plan: string | null;
  suscripcion: string | null;
  nivel: string | null;
}

// Sprint 20.8 (D013-EC-76) — modal payload now carries real Anthropic
// per-runner data via fan-out. Pre-S20.8 it returned RunnerSnapshot
// rows with the heuristic `cap_5h_residual_pct` / `weekly_hours_*`
// projection; that data still flows through `fetchRunners()` for the
// admin panel + scheduler ranking, but the modal switched to
// authoritative numbers.
export function fetchRunnersUsage(opts?: {
  signal?: AbortSignal;
  // Sprint rate-limit-banner-ssot — when true, bypasses the backend
  // TTL cache and forces a fresh fan-out to Anthropic. Used by the
  // lazy poll hook while the banner is active so we don't sit on a
  // stale 60 s window when ground truth has likely shifted.
  fresh?: boolean;
}): Promise<{
  runners: RunnerUsageEntry[];
}> {
  const qs = opts?.fresh ? '?force=true' : '';
  return api(`/api/runners/usage${qs}`, { signal: opts?.signal });
}

export function drainRunner(id: string): Promise<{ runner: RunnerSnapshot; dispatchedToRunner: boolean }> {
  return api(`/api/runners/${encodeURIComponent(id)}/drain`, { method: 'POST' });
}

export function undrainRunner(id: string): Promise<{ runner: RunnerSnapshot }> {
  return api(`/api/runners/${encodeURIComponent(id)}/undrain`, { method: 'POST' });
}

export function clearRunnerUnavailable(id: string): Promise<{ runner: RunnerSnapshot }> {
  return api(`/api/runners/${encodeURIComponent(id)}/clear-unavailable`, { method: 'POST' });
}

// ---------- Agents (creation) ----------------------------------------------
//
// Sprint 5.6 — composer-first agent creation. `name` is optional; when
// omitted, the backend assigns DEFAULT_AGENT_NAME and fires Haiku-driven
// auto-rename in the background. `templateId` is gone (templates feature
// removed by ADR D-001).

export interface CreateAgentPayload {
  name?: string;
  prompt: string;
  model?: string;
  effort?: EffortLevel;
  // S6.5 — sets the new agent's mode at spawn time. Optional so existing
  // tests/fixtures keep compiling; backend defaults to 'agent'.
  permissionMode?: PermissionMode;
  // Sprint 11b.1 — workspace assignment.
  projectId?: string;
}

// Sprint 12 (D-008 §2.3) — "+" creates an empty agent row immediately
// (no first-prompt requirement). The backend creates the DB row and
// returns it; CLI spawn is deferred to the first user message.
export async function createEmptyAgent(opts?: {
  model?: string;
  effort?: string;
  permissionMode?: string;
  projectId?: string;
  runnerId?: string;
}): Promise<Agent> {
  const res = await api<{ agent: Agent }>('/api/agents/empty', {
    method: 'POST',
    body: opts ?? {},
  });
  return res.agent;
}

export async function createAgent(payload: CreateAgentPayload): Promise<Agent> {
  const res = await api<{ agent: Agent }>('/api/agents', {
    method: 'POST',
    body: payload,
  });
  return res.agent;
}

// Sprint 5.6 (B7) — mid-chat config patch. Triggers a transparent --resume
// respawn server-side; we just receive the new agent row.
// S6.5 — `permissionMode` joins the patch fields. Same respawn machinery,
// new spawn flag (`--permission-mode bypassPermissions` ↔ `plan`).
export async function patchAgentConfig(
  id: string,
  fields: { model?: string; effort?: EffortLevel; permissionMode?: PermissionMode },
): Promise<Agent> {
  const res = await api<{ agent: Agent }>(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: fields,
  });
  return res.agent;
}

// Sprint 5.7 (D32) — Undo path. Backend accepts `deletedAt: null` only; any
// other value is rejected. Clearing the timestamp re-inserts the agent into
// `list()` and the sidebar repopulates via the `agent:updated` WebSocket
// event (store subscribers also run on the returned row as a fallback).
export async function undeleteAgent(id: string): Promise<Agent> {
  const res = await api<{ agent: Agent }>(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { deletedAt: null },
  });
  return res.agent;
}

// S6.5 — permission resolution.
//
// Body shapes:
//   • Allow once / Deny:        { decision: 'allow' | 'deny', reason? }
//   • Allow always:             { decision: 'allow_always' }   ← inserts allowlist rule server-side
//   • AskUserQuestion answer:   { decision: 'allow', answers: { [questionText]: chosenLabel | string[] } }
//
// Backend returns 204 on success, 410 Gone if the request is already
// resolved (idempotent — UI treats that as "someone else clicked first").
export type PermissionDecisionInput =
  | { decision: 'allow'; reason?: string; answers?: Record<string, string | string[]> }
  | { decision: 'deny'; reason?: string }
  | { decision: 'allow_always'; reason?: string };

export async function resolvePermission(
  agentId: string,
  requestId: string,
  body: PermissionDecisionInput,
): Promise<void> {
  await api(
    `/api/agents/${encodeURIComponent(agentId)}/permissions/${encodeURIComponent(requestId)}`,
    { method: 'POST', body },
  );
}

// S6.5 — global allowlist surface (Settings → "Allowed forever"). Phase 6
// renders the list; today these helpers exist so PermissionCard's tests
// can stub them and Phase 6 doesn't have to refactor the API layer.
export interface AllowlistRule {
  id: string;
  toolName: string;
  pattern: string;
  createdAt: string;
  createdByAgentId: string | null;
}

export async function fetchAllowlistRules(): Promise<AllowlistRule[]> {
  const res = await api<{ rules: AllowlistRule[] }>('/api/permissions/allowlist');
  return res.rules;
}

export async function deleteAllowlistRule(id: string): Promise<void> {
  await api(`/api/permissions/allowlist/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// On-demand Claude Max usage — proxied through the backend to avoid
// exposing the OAuth token to the browser.
export interface UsageBucket {
  utilization: number;
  resets_at: string;
}

// Sprint usage-limits-v2 (2026-07-08) — canonical `limits[]` entry.
// Anthropic's `/api/oauth/usage` now surfaces every active limit
// (session / weekly_all / weekly_scoped / future kinds) here. Legacy
// buckets above stay as fallback (D2) when the runner reports v9 or
// the array is absent.
export interface UsageLimitEntry {
  kind: string;
  group?: string;
  percent: number;
  severity?: string;
  resets_at: string;
  modelName?: string;
  isActive?: boolean;
}

export interface UsageData {
  five_hour: UsageBucket | null;
  seven_day: UsageBucket | null;
  seven_day_sonnet: UsageBucket | null;
  seven_day_opus: UsageBucket | null;
  extra_usage: (UsageBucket & { spent_usd?: number; limit_usd?: number }) | null;
  limits?: UsageLimitEntry[];
  pago_por_uso?: { activo: boolean; gastado: number; limite: number | null; moneda: string | null } | null;
}

// Sprint 6.7 (D36) — composer autocomplete. Returns the scored + category-
// filtered list from /api/workdir/index. Consumers (useAutocomplete) pass
// an AbortSignal so stale requests don't overwrite fresher ones.
export async function fetchWorkdirIndex(opts: {
  trigger: '@' | '/';
  q: string;
  limit?: number;
  signal?: AbortSignal;
}): Promise<WorkdirIndexResponse> {
  const params = new URLSearchParams();
  params.set('trigger', opts.trigger);
  if (opts.q) params.set('q', opts.q);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  return api<WorkdirIndexResponse>(
    `/api/workdir/index?${params.toString()}`,
    { signal: opts.signal },
  );
}
